import { useCallback, useDeferredValue, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { PeriodTabs } from '../components/PeriodTabs';
import { RaporTablosu } from '../components/rapor-ekrani/RaporTablosu';
import { RaporHatasi } from '../components/rapor-ekrani/RaporHatasi';
import { FILTRE_GIRIS, FiltreAlani, RaporFiltreBari, gecerliTarih } from '../components/rapor-ekrani/RaporFiltreBari';
import { useDoluYukseklik } from '../components/rapor-ekrani/useDoluYukseklik';
import { useCekmeceSecimi } from '../components/rapor-ekrani/useCekmeceSecimi';
import { ekranKaynagi, useRaporSorgu } from '../components/rapor-ekrani/useRaporSorgu';
import { useDarEkran } from '../components/rapor-ekrani/useDarEkran';
import { kucukTr, tarihMetni } from '../components/rapor-ekrani/bicim';
import type { RaporKolonu } from '../components/rapor-ekrani/kolon';
import { GruplamaAlanlari, UrunGrubuSecimi } from '../components/stok-rapor/StokSuzgecleri';
import { StokCikisCekmecesi } from '../components/stok-rapor/StokCekmeceleri';
import { useGruplama } from '../components/stok-rapor/useGruplama';
import {
  CIKIS_TURU_ADI, CIKIS_TURU_SECENEKLERI, cikislariTopla, grupKolonlari, grupSayisi, gruplamaEtiketi, karYuzdesi,
  stokCikisSuz, urunGruplari,
  type CikisTuru, type HamStokCikisSatiri, type StokCikisSatiri,
} from '../components/stok-rapor/tipler';
import type { EkranProps } from '../ekranKayitDefteri';

const SAYI = new Intl.NumberFormat('tr-TR');
/** Varsayılan dönem: bugün dahil son 30 gün. */
const VARSAYILAN_GUN = 30;

/**
 * S345: Stok Çıkış Raporu (ERP12 rapor ekranları; onaylı demo `tasks/s334/erp12-rapor-demosu.html`, `cikis`).
 * Liste `liste` ds'inden (`@BASTARIH`, `@BITTARIH`; satır = ürün × fiş türü), satıra basınca açılan
 * çekmece `fisler` ds'inden beslenir; kaynaklar `firm_panel_screens.config.kaynaklar` sözlüğünden çözülür.
 *
 * "Çıkış türü" İSTEMCİDE süzer: ds satırları `satis_mi` ile ayrılır ve ÜRÜN bazında toplanır (sorgu
 * yinelenmez). Ürün grubu, arama ve gruplama da istemcidedir; dönem değişince liste yeniden sorgulanır
 * ve önceki liste o arada ekranda kalır. Maliyet BUGÜNKÜ son alış fiyatıdır (bilgi satırı bunu söyler).
 * Çekmecedeki ürün URL'de durur (`?stok=<id>`).
 *
 * LOKASYON FİLTRESİ YOK: lokasyon seçim listesini veren bir ds yok. `@LOKASYON` gönderilmez; ds boş
 * değeri "tümü" sayar.
 *
 * TELEFON (<= 767 px): liste KART (üstte ürün adı, sağında tutar; altında kod, miktar, alış tutarı, kâr,
 * kâr %). Yapışık barda yalnız çıkış türü seçicisi; ötekiler katlanır "Filtreler"in içinde.
 */
export default function StokCikisRaporu({ screen }: EkranProps) {
  const listeKaynagi = ekranKaynagi(screen, 'liste');
  const fislerKaynagi = ekranKaynagi(screen, 'fisler');
  const telefon = useDarEkran();
  const doluRef = useDoluYukseklik<HTMLDivElement>(telefon ? 'enAz' : 'yukseklik');

  const [tur, setTur] = useState<CikisTuru>('tumu');
  const [urunGrubuSecimi, setUrunGrubu] = useState('');
  const [ara, setAra] = useState('');
  // Tarihler iki durumdur: `...Giris` alanda yazan, öteki sorguya giden (elle yazarken ara değerler gitmez).
  const [varsayilan] = useState(() => {
    const bugun = dayjs();
    return { bas: bugun.subtract(VARSAYILAN_GUN - 1, 'day').format('YYYY-MM-DD'), bit: bugun.format('YYYY-MM-DD') };
  });
  const [bas, setBas] = useState(varsayilan.bas);
  const [bit, setBit] = useState(varsayilan.bit);
  const [basGiris, setBasGiris] = useState(bas);
  const [bitGiris, setBitGiris] = useState(bit);
  const ters = bas > bit;

  const q = useRaporSorgu<HamStokCikisSatiri>(listeKaynagi, { '@BASTARIH': bas, '@BITTARIH': bit }, !ters);
  const ham = q.data;
  // Dönemde çıkışı olan ürünlerin TAMAMI: gruplama ve ürün grubu seçenekleri çıkış türüyle oynamasın.
  const tumUrunler = useMemo(() => cikislariTopla(ham ?? [], 'tumu'), [ham]);
  const tum = useMemo(() => (tur === 'tumu' ? tumUrunler : cikislariTopla(ham ?? [], tur)), [ham, tur, tumUrunler]);
  const gruplar = useMemo(() => urunGruplari(tumUrunler), [tumUrunler]);
  const urunGrubu = gruplar.includes(urunGrubuSecimi) ? urunGrubuSecimi : '';
  const araGecikmeli = useDeferredValue(ara);
  const suzulen = useMemo(() => stokCikisSuz(tum, urunGrubu, araGecikmeli), [tum, urunGrubu, araGecikmeli]);

  const gruplama = useGruplama<StokCikisSatiri>(tumUrunler, '');
  const { dolu } = gruplama;
  const grupAdedi = useMemo(() => grupSayisi(suzulen, gruplama.alan), [suzulen, gruplama.alan]);

  const kolonlar = useMemo<RaporKolonu<StokCikisSatiri>[]>(() => [
    { id: 'stok_kodu', baslik: 'Stok Kodu', tur: 'kod', deger: (s) => s.kod, kart: 'alt' },
    { id: 'urun', baslik: 'Ürün', tur: 'metin', deger: (s) => s.ad, esnek: true, kart: 'baslik' },
    // S346: 13 kolon 1440'ta sığmıyordu (Kâr, Kâr %, Eldeki Miktar kaydırmada kalıyordu). Marka kolonu,
    // KDV Dahil Tutar ve Son Alış Fiyatı tablodan çıktı; marka gruplama seçeneği olarak duruyor.
    ...grupKolonlari<StokCikisSatiri>(dolu).filter((k) => k.id !== 'marka'),
    { id: 'miktar', baslik: 'Miktar', tur: 'sayi', deger: (s) => s.miktar, toplam: true, kart: 'ikincil' },
    { id: 'ort_fiyat', baslik: 'Ort. Fiyat', tur: 'para', deger: (s) => s.ortFiyat, kart: 'gizli' },
    { id: 'tutar', baslik: 'Tutar', tur: 'para', deger: (s) => s.tutar, toplam: true, kart: 'olcu' },
    { id: 'alis_tutari', baslik: 'Alış Tutarı', tur: 'para', deger: (s) => s.alis, toplam: true, kart: 'ikincil' },
    { id: 'kar', baslik: 'Kâr', tur: 'para', deger: (s) => s.kar, toplam: true, kart: 'ikincil' },
    {
      id: 'kar_yuzde', baslik: 'Kâr %', tur: 'yuzde', deger: (s) => s.karYuzde, kart: 'ikincil',
      // Toplamda (ve grup ara toplamında) oran, satır oranlarının toplamı değil TOPLAMLARIN oranıdır.
      toplam: (satirlar) => karYuzdesi(satirlar.reduce((t, s) => t + s.kar, 0), satirlar.reduce((t, s) => t + s.alis, 0)),
    },
    { id: 'eldeki_miktar', baslik: 'Eldeki Miktar', tur: 'sayi', deger: (s) => s.elde, kart: 'gizli' },
  ], [dolu]);

  const secim = useCekmeceSecimi('stok');
  const seciliStok = useMemo(
    () => (secim.secili ? tum.find((s) => s.id === secim.secili) : undefined),
    [tum, secim.secili],
  );
  const { ac } = secim;
  const satirTiklandi = useCallback((s: StokCikisSatiri) => ac(s.id), [ac]);

  const ilkYukleme = q.isLoading;
  const yenileniyor = q.isFetching && !ilkYukleme;

  const aramaGirisi = (
    <input
      // Telefonda `search` DEĞİL `text`: iOS `search` girdisine 24 px'lik kendi temizleme düğmesini koyar.
      type={telefon ? 'text' : 'search'}
      inputMode="search"
      className={telefon ? `${FILTRE_GIRIS} w-full` : `${FILTRE_GIRIS} w-[220px] max-w-full`}
      placeholder="Stok kodu ya da ürün"
      aria-label={telefon ? 'Stok kodu ya da ürün adında ara' : undefined}
      value={ara}
      onChange={(e) => setAra(e.target.value)}
    />
  );
  const tarihAlani = (etiket: string, giris: string, setGiris: (v: string) => void, uygula: (v: string) => void, gecerli: string) => (
    <FiltreAlani etiket={etiket}>
      <input
        type="date"
        className={FILTRE_GIRIS}
        value={giris}
        onChange={(e) => {
          setGiris(e.target.value);
          if (gecerliTarih(e.target.value)) uygula(e.target.value);
        }}
        onBlur={() => { if (!gecerliTarih(giris)) setGiris(gecerli); }}
      />
    </FiltreAlani>
  );

  const donem = `${tarihMetni(bas)} - ${tarihMetni(bit)}`;
  const grupNotu = gruplama.alan === '' ? '' : `${SAYI.format(grupAdedi)} ${kucukTr(gruplamaEtiketi(gruplama.alan))}`;
  const etkinAdlar = [
    (bas !== varsayilan.bas || bit !== varsayilan.bit) && 'Dönem',
    urunGrubu !== '' && 'Ürün grubu',
  ].filter((x): x is string => !!x);
  const bilgiYok = ilkYukleme || !listeKaynagi || ters;

  return (
    <div
      ref={doluRef}
      data-testid="stok-cikis-raporu"
      className={telefon ? 'flex flex-col gap-2 -mb-4 rk-kenar-16' : 'flex flex-col gap-2 min-h-[320px]'}
    >
      <RaporFiltreBari
        testid="stok-cikis-filtre-bar"
        birincil={
          <FiltreAlani etiket="Çıkış türü" grup>
            <PeriodTabs value={tur} onChange={setTur} secenekler={CIKIS_TURU_SECENEKLERI} kompakt />
          </FiltreAlani>
        }
        etkinAdlar={etkinAdlar}
        ozet={bilgiYok ? undefined : yenileniyor ? 'Yenileniyor…' : `${SAYI.format(suzulen.length)} ürün · ${donem}`}
      >
        {tarihAlani('Başlangıç', basGiris, setBasGiris, setBas, bas)}
        {tarihAlani('Bitiş', bitGiris, setBitGiris, setBit, bit)}
        {gruplar.length > 0 && <UrunGrubuSecimi deger={urunGrubu} gruplar={gruplar} onDegis={setUrunGrubu} />}
        {!telefon && <FiltreAlani etiket="Ara">{aramaGirisi}</FiltreAlani>}
        <GruplamaAlanlari alan={gruplama.alan} dolu={dolu} onSec={gruplama.sec} onAciklik={gruplama.setAciklik} />
      </RaporFiltreBari>

      {/* Telefonda bilgi satırı kısadır (sayı ve dönem "Filtreler" başlığında); maliyet notu burada kalır. */}
      <div className="text-[13px] text-ink-500 min-h-[20px]" data-testid="stok-cikis-bilgi">
        {bilgiYok ? (
          <>&nbsp;</>
        ) : telefon ? (
          <>{CIKIS_TURU_ADI[tur]} · maliyet = bugünkü son alış</>
        ) : (
          <>
            <b className="font-semibold text-ink-900">{SAYI.format(suzulen.length)}</b> ürün
            {grupNotu && <> · {grupNotu} (kartta boş olanlar "(Tanımsız)" grubunda)</>}
            {' · '}{donem} · {CIKIS_TURU_ADI[tur]} · maliyet = bugünkü son alış
            {yenileniyor && <span className="ml-2 text-ink-400" role="status">Yenileniyor…</span>}
          </>
        )}
      </div>

      {ters ? (
        <RaporHatasi testid="stok-cikis-ters" mesaj="Başlangıç tarihi bitiş tarihinden sonra olamaz." />
      ) : !listeKaynagi ? (
        <RaporHatasi testid="stok-cikis-kaynaksiz" mesaj="Bu firmada stok çıkış raporu kaynağı tanımlı değil." />
      ) : q.isError ? (
        <RaporHatasi testid="stok-cikis-hata" mesaj="Stok çıkış raporu yüklenemedi." onTekrar={() => q.refetch()} />
      ) : (
        <RaporTablosu
          testid="stok-cikis-tablosu"
          etiket="Stok Çıkış Raporu"
          kolonlar={kolonlar}
          satirlar={suzulen}
          satirKimligi={(s) => s.id}
          onSatirTikla={satirTiklandi}
          seciliKimlik={seciliStok?.id ?? null}
          grup={gruplama.grup}
          grupAcikligi={gruplama.aciklik}
          onGrupAcikligi={gruplama.setAciklik}
          hamSatirSayisi={tum.length}
          // Çıkış türü satır sayısını azaltmadan da toplamı daraltır (ürünün yalnız satış satırları kalır).
          suzuldu={tur !== 'tumu'}
          bosMesaj={tumUrunler.length === 0 ? 'Bu dönemde çıkışı olan ürün yok.' : 'Bu süzgeçle kayıt yok.'}
          yukleniyor={ilkYukleme}
          yenileniyor={yenileniyor}
          kartAraclari={aramaGirisi}
        />
      )}

      <StokCikisCekmecesi stok={ters ? undefined : seciliStok} kaynak={fislerKaynagi} bas={bas} bit={bit} tur={tur} onKapat={secim.kapat} />
    </div>
  );
}
